import { useApolloClient, useMutation } from '@apollo/client/react'
import {
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
} from '@sentry/tanstackstart-react'
import { useNavigate } from '@tanstack/react-router'
import { useAuth } from '@web/src/hooks/use-auth'
import { authClient } from '@web/src/lib/auth/auth-client'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { useTranslation } from '@web/src/lib/intl/react'
import {
  cleanUpPushAfterSessionEnded,
  GET_PUSH_PUBLIC_KEY,
  hasSignedInMarker,
  REMOVE_PUSH_SUBSCRIPTION,
  SAVE_PUSH_SUBSCRIPTION,
  setSignedInMarker,
  toPushSubscriptionInput,
} from '@web/src/lib/pwa/push-subscription'
import {
  ensureServiceWorkerRegistration,
  isServiceWorkerEnabled,
  withTimeout,
} from '@web/src/lib/pwa/service-worker-registration'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'

const PUSH_SUBSCRIBE_TIMEOUT_MS = 25_000
const PUSH_SAVE_TIMEOUT_MS = 20_000
// getSubscription() overlapping the start of subscribe() makes iPadOS abort the subscription
const PUSH_SUBSCRIPTION_POLL_DELAY_MS = 3000
const PUSH_SUBSCRIPTION_POLL_MS = 1000
const PUSH_DIAGNOSTIC_TIMEOUT_MS = 3000

class PushPermissionDeniedError extends Error {}

type PushNotificationContextType = {
  isPushSupported: boolean
  isPushReady: boolean
  isPushEnabled: boolean
  subscription: PushSubscription | null
  subscribeToPush: () => Promise<boolean>
  unsubscribeFromPush: () => Promise<boolean>
  isLoading: boolean
  updateBadgeCount: (count: number) => void
}

const PushNotificationContext = createContext<PushNotificationContextType>({
  isPushSupported: false,
  isPushReady: false,
  isPushEnabled: false,
  subscription: null,
  subscribeToPush: async () => false,
  unsubscribeFromPush: async () => false,
  isLoading: false,
  updateBadgeCount: () => {
    /* noop */
  },
})

export const PushNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation()
  const [isPushSupported, setIsPushSupported] = useState(false)
  const [isPushReady, setIsPushReady] = useState(false)
  const [isPushEnabled, setIsPushEnabled] = useState(false)
  const [subscription, setSubscription] = useState<PushSubscription | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const client = useApolloClient()
  const { currentUser, isImpersonating, isSuccess: isSessionLoaded } = useAuth()
  const currentUserId = currentUser?.id
  const isSignedOut = isSessionLoaded && !currentUserId
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null)
  const publicKeyRef = useRef<string | null>(null)
  const subscriptionEndpointRef = useRef<string | null>(null)
  const pushActionInFlightRef = useRef(false)

  const [savePushSubscription] = useMutation(SAVE_PUSH_SUBSCRIPTION)
  const [removePushSubscription] = useMutation(REMOVE_PUSH_SUBSCRIPTION)

  // `controller` is null after a hard reload, so prefer the active worker of the registration
  const updateBadgeCount = (count: number) => {
    if (!isServiceWorkerEnabled()) {
      return
    }

    const worker = registrationRef.current?.active ?? navigator.serviceWorker.controller
    worker?.postMessage({
      type: 'UPDATE_BADGE_COUNT',
      count,
    })
  }

  // Handle messages from service worker
  useEffect(() => {
    if (!isServiceWorkerEnabled()) {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'NOTIFICATION_CLICK') {
        return
      }

      const url: unknown = event.data.notification?.url
      if (!isAppPath(url)) {
        return
      }

      // The worker opens the URL itself when no window acknowledges the click
      event.ports[0]?.postMessage({ type: 'NOTIFICATION_CLICK_ACK' })

      navigate({ href: url }).catch((error: unknown) => {
        console.error('Error navigating to notification URL:', error)
        toast.error(t('Unable to navigate to notification destination'))
      })
    }

    navigator.serviceWorker.addEventListener('message', handleMessage)

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage)
    }
  }, [navigate, t])

  // Check support, warm the worker plus VAPID key, and re-sync an existing subscription with the
  // server whenever the signed-in user changes. iPadOS drops the click's user activation if
  // subscribe() waits on the worker or key, and the server row may be gone or owned by another user.
  useEffect(() => {
    if (!(isServiceWorkerEnabled() && 'PushManager' in window)) {
      setIsPushSupported(false)
      return
    }

    // Saving during impersonation would move this device's subscription to the impersonated profile
    setIsPushSupported(!isImpersonating)

    // The switch stays disabled until this effect has cached the worker and key. A cold tap would
    // have to wait for them, and iPadOS would no longer treat that tap as the permission gesture.
    if (currentUserId && !(registrationRef.current?.active && publicKeyRef.current)) {
      setIsPushReady(false)
    } else if (!currentUserId) {
      setIsPushReady(true)
    }

    let cancelled = false
    // A subscribe or unsubscribe started from the switch owns the push state until it settles
    const isStale = () => cancelled || pushActionInFlightRef.current

    const preparePush = async () => {
      try {
        const registration = await ensureServiceWorkerRegistration()
        if (isStale()) return

        registrationRef.current = registration
        let existingSubscription = await registration.pushManager.getSubscription()
        if (isStale()) return

        if (isImpersonating) {
          subscriptionEndpointRef.current = null
          setSubscription(null)
          setIsPushEnabled(false)
          return
        }

        if (!currentUserId) {
          subscriptionEndpointRef.current = existingSubscription?.endpoint ?? null
          setSubscription(existingSubscription)
          setIsPushEnabled(false)
          setIsPushReady(true)

          // A session that ended without sign-out leaves the previous user's pushes and cached pages behind
          if (!(isSignedOut && (existingSubscription || hasSignedInMarker()))) return
          if (!(await isSessionEnded()) || isStale()) return

          await cleanUpPushAfterSessionEnded(registration)
          if (!isStale()) {
            subscriptionEndpointRef.current = null
            setSubscription(null)
          }
          return
        }

        setSignedInMarker(true)

        const publicKey = await loadPublicKey()
        if (isStale()) return

        // Pushes to a subscription made with a rotated server key fail with 403, which the jobs worker never cleans up
        const applicationServerKey = urlBase64ToUint8Array(publicKey)
        if (existingSubscription && hasDifferentServerKey(existingSubscription, applicationServerKey)) {
          await removePushSubscription({
            variables: { input: { subscription: toPushSubscriptionInput(existingSubscription) } },
          }).catch((error: unknown) => {
            console.error('Error removing push subscription with an outdated server key:', error)
          })
          await existingSubscription.unsubscribe()
          existingSubscription = await resubscribeWithGrantedPermission(registration.pushManager, applicationServerKey)
          if (isStale()) return
        }

        subscriptionEndpointRef.current = existingSubscription?.endpoint ?? null
        // Subscribe on the next tap can start immediately, while iPadOS still counts the tap as a gesture
        setIsPushReady(true)
        setSubscription(existingSubscription)

        if (!existingSubscription) {
          setIsPushEnabled(false)
          return
        }

        await savePushSubscription({
          variables: { input: { subscription: toPushSubscriptionInput(existingSubscription) } },
        })
        if (isStale()) return

        setIsPushEnabled(true)
      } catch (error) {
        if (!isStale()) {
          setIsPushEnabled(false)
          // Leave the switch usable so a later tap can retry once the worker or key recovers
          setIsPushReady(true)
        }
        console.error('Error preparing push notifications:', error)
      }
    }

    preparePush()

    return () => {
      cancelled = true
    }
  }, [currentUserId, isImpersonating, isSignedOut])

  const loadPublicKey = async (): Promise<string> => {
    if (publicKeyRef.current) {
      return publicKeyRef.current
    }

    const { data } = await client.query({
      query: GET_PUSH_PUBLIC_KEY,
      fetchPolicy: 'network-only',
    })
    const publicKey = data?.getPushPublicKey?.publicKey

    if (!publicKey) {
      throw new Error('Failed to get public key')
    }

    publicKeyRef.current = publicKey
    return publicKey
  }

  const resolveServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration> => {
    const cached = registrationRef.current
    if (cached?.active) {
      return cached
    }

    const registration = await ensureServiceWorkerRegistration()
    registrationRef.current = registration
    return registration
  }

  // Function to subscribe to push notifications
  const subscribeToPush = async (): Promise<boolean> => {
    if (!isPushSupported || pushActionInFlightRef.current) {
      return false
    }

    if (typeof Notification === 'undefined') {
      return false
    }

    if (Notification.permission === 'denied') {
      toast.error(t('Failed to enable push notifications, please make sure your browser is set to allow notifications'))
      return false
    }

    // iPadOS forgets the tap's user activation at the first await, and requestPermission()
    // spends that activation itself. subscribe() shows the prompt, so it has to start now,
    // using the worker and key cached before the tap.
    pushActionInFlightRef.current = true
    setIsLoading(true)

    let pushSubscription: PushSubscription | null = null
    let step: PushSubscribeStep = 'subscribe'
    const startedAt = Date.now()
    const permissionAtTap = Notification.permission
    const registration = registrationRef.current
    const publicKey = publicKeyRef.current
    const usedCachedSetup = Boolean(registration?.active && publicKey)

    try {
      const previousEndpoint = subscriptionEndpointRef.current

      if (registration?.active && publicKey) {
        // Evaluating this call starts subscribe() before the await yields
        pushSubscription = await subscribeWithServerKey(
          registration.pushManager,
          urlBase64ToUint8Array(publicKey),
          previousEndpoint
        )
      } else {
        const resolvedRegistration = await resolveServiceWorkerRegistration()
        const resolvedKey = urlBase64ToUint8Array(await loadPublicKey())
        pushSubscription = await subscribeWithServerKey(resolvedRegistration.pushManager, resolvedKey, previousEndpoint)
      }

      step = 'save'
      await withTimeout(
        savePushSubscription({
          variables: {
            input: {
              subscription: toPushSubscriptionInput(pushSubscription),
            },
          },
        }),
        PUSH_SAVE_TIMEOUT_MS,
        'Timed out saving push subscription'
      )

      subscriptionEndpointRef.current = pushSubscription.endpoint
      setSubscription(pushSubscription)
      setIsPushEnabled(true)
      toast.success(t('Push notifications enabled!'))

      return true
    } catch (error) {
      console.error('Error subscribing to push:', error)
      // Console logs are not sent to Sentry, and on iPadOS this is the only way to see why subscribing failed
      reportPushSubscribeError(
        error,
        {
          step,
          elapsedMs: Date.now() - startedAt,
          permissionAtTap,
          permissionNow: Notification.permission,
          usedCachedSetup,
          endpointHost: pushSubscription ? new URL(pushSubscription.endpoint).host : null,
        },
        registrationRef.current
      ).catch((reportError: unknown) => {
        console.error('Error reporting push subscribe failure:', reportError)
      })

      // Otherwise the next page load re-saves this unsaved subscription and push turns on after a failure toast
      if (pushSubscription) {
        await pushSubscription.unsubscribe().catch((unsubscribeError: unknown) => {
          console.error('Error unsubscribing unsaved push subscription:', unsubscribeError)
        })
        subscriptionEndpointRef.current = null
      }

      if (isPushPermissionError(error)) {
        toast.error(
          t('Failed to enable push notifications, please make sure your browser is set to allow notifications')
        )
      } else {
        toast.error(t('Failed to enable push notifications, please try again'))
      }
      return false
    } finally {
      pushActionInFlightRef.current = false
      setIsLoading(false)
    }
  }

  // Function to unsubscribe from push notifications
  const unsubscribeFromPush = async (): Promise<boolean> => {
    if (!subscription || pushActionInFlightRef.current) {
      return false
    }

    pushActionInFlightRef.current = true
    setIsLoading(true)

    try {
      await removePushSubscription({
        variables: {
          input: {
            subscription: toPushSubscriptionInput(subscription),
          },
        },
      })

      await subscription.unsubscribe()

      subscriptionEndpointRef.current = null
      setSubscription(null)
      setIsPushEnabled(false)
      updateBadgeCount(0)
      toast.success(t('Push notifications disabled'))

      return true
    } catch (error) {
      console.error('Error unsubscribing from push:', error)
      toast.error(t('Failed to disable push notifications'))
      return false
    } finally {
      pushActionInFlightRef.current = false
      setIsLoading(false)
    }
  }

  return (
    <PushNotificationContext.Provider
      value={{
        isPushSupported,
        isPushReady,
        isPushEnabled,
        subscription,
        subscribeToPush,
        unsubscribeFromPush,
        isLoading,
        updateBadgeCount,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  )
}

function isAppPath(url: unknown): url is string {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')
}

type PushSubscribeStep = 'subscribe' | 'save'

type WindowWithPushManager = Window & { pushManager?: PushManager }

function isSentryEnabled(): boolean {
  const appConfig = getAppConfig()
  return Boolean(appConfig.SENTRY_DSN_WEB) && !appConfig.SENTRY_DISABLE
}

async function reportPushSubscribeError(
  error: unknown,
  details: {
    step: PushSubscribeStep
    elapsedMs: number
    permissionAtTap: NotificationPermission
    permissionNow: NotificationPermission
    usedCachedSetup: boolean
    endpointHost: string | null
  },
  registration: ServiceWorkerRegistration | null
): Promise<void> {
  if (!isSentryEnabled()) return

  const diagnostics = details.step === 'subscribe' ? await collectPushDiagnostics(registration) : {}

  sentryCaptureException(error, {
    tags: {
      feature: 'push-notifications',
      pushStep: details.step,
      pushErrorName: error instanceof Error ? error.name : typeof error,
      pushPermissionAtTap: details.permissionAtTap,
    },
    extra: {
      ...details,
      ...diagnostics,
      displayModeStandalone: window.matchMedia('(display-mode: standalone)').matches,
      userAgent: navigator.userAgent,
    },
  })
}

// A subscribe() that settles after the timeout shows whether iPadOS is slow or never answers at all
function reportLateSubscribeResult(details: { elapsedMs: number; outcome: string; endpointHost?: string }): void {
  if (!isSentryEnabled()) return

  sentryCaptureMessage('Push subscribe settled after timeout', {
    level: 'warning',
    tags: { feature: 'push-notifications', pushLateOutcome: details.outcome },
    extra: details,
  })
}

// Each browser call is time-boxed because the same push service that hung subscribe() can hang these too
async function collectPushDiagnostics(
  registration: ServiceWorkerRegistration | null
): Promise<Record<string, unknown>> {
  const probe = <T,>(promise: Promise<T>): Promise<T | string> =>
    withTimeout(promise, PUSH_DIAGNOSTIC_TIMEOUT_MS, 'timed out').catch(
      (error: unknown) => `error: ${error instanceof Error ? `${error.name} ${error.message}` : String(error)}`
    )

  const describeSubscription = (subscription: PushSubscription | null | string) => {
    if (subscription === null || typeof subscription === 'string') return subscription
    return {
      endpointHost: new URL(subscription.endpoint).host,
      hasServerKey: Boolean(subscription.options?.applicationServerKey),
    }
  }

  const windowPushManager = (window as WindowWithPushManager).pushManager
  const registrations = await probe(navigator.serviceWorker.getRegistrations())

  return {
    registrationScope: registration?.scope ?? null,
    activeWorkerState: registration?.active?.state ?? null,
    activeWorkerScript: registration?.active?.scriptURL ?? null,
    hasInstallingWorker: Boolean(registration?.installing),
    hasWaitingWorker: Boolean(registration?.waiting),
    controllerScript: navigator.serviceWorker.controller?.scriptURL ?? null,
    registrationScopes: Array.isArray(registrations) ? registrations.map(({ scope }) => scope) : registrations,
    registrationPermissionState: registration
      ? await probe(registration.pushManager.permissionState({ userVisibleOnly: true }))
      : null,
    registrationSubscription: registration
      ? describeSubscription(await probe(registration.pushManager.getSubscription()))
      : null,
    hasWindowPushManager: Boolean(windowPushManager),
    windowPermissionState: windowPushManager
      ? await probe(windowPushManager.permissionState({ userVisibleOnly: true }))
      : null,
    windowSubscription: windowPushManager
      ? describeSubscription(await probe(windowPushManager.getSubscription()))
      : null,
    navigatorStandalone: (navigator as Navigator & { standalone?: boolean }).standalone ?? null,
    visibilityState: document.visibilityState,
    online: navigator.onLine,
  }
}

function isPushPermissionError(error: unknown): boolean {
  return (
    error instanceof PushPermissionDeniedError || (error instanceof DOMException && error.name === 'NotAllowedError')
  )
}

// subscribe() rejects with InvalidStateError when the browser already holds a subscription
// for a different VAPID key, e.g. after the server key was rotated.
// The first subscribe() call happens before this function awaits, so a tap's user activation
// is still available to iPadOS when the caller has not awaited anything yet.
async function subscribeWithServerKey(
  pushManager: PushManager,
  applicationServerKey: BufferSource,
  previousEndpoint: string | null
): Promise<PushSubscription> {
  try {
    return await subscribePushManager(pushManager, applicationServerKey, previousEndpoint)
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'InvalidStateError')) {
      throw error
    }

    const existing = await pushManager.getSubscription()
    if (!existing || applicationServerKeysMatch(existing, applicationServerKey)) {
      throw error
    }

    await existing.unsubscribe()
    return subscribePushManager(pushManager, applicationServerKey, null)
  }
}

// Permission is already granted, so no click is needed; browsers that still require one leave push off
async function resubscribeWithGrantedPermission(
  pushManager: PushManager,
  applicationServerKey: BufferSource
): Promise<PushSubscription | null> {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return null
  }

  try {
    return await subscribePushManager(pushManager, applicationServerKey, null)
  } catch (error) {
    console.error('Error resubscribing with the current server key:', error)
    return null
  }
}

// After the user allows notifications, iPadOS and Chrome can leave
// pushManager.subscribe() pending even though the subscription exists.
// Polling starts late: an earlier getSubscription() races WebKit and aborts subscribe().
function subscribePushManager(
  pushManager: PushManager,
  applicationServerKey: BufferSource,
  previousEndpoint: string | null
): Promise<PushSubscription> {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now()
    let settled = false
    let resolved = false
    let pollId = 0
    let timeoutId = 0
    let pollDelayId = 0

    const stopTimers = () => {
      window.clearTimeout(timeoutId)
      window.clearTimeout(pollDelayId)
      window.clearInterval(pollId)
    }

    const finish = (subscription: PushSubscription) => {
      if (settled) return
      settled = true
      resolved = true
      stopTimers()
      resolve(subscription)
    }

    const fail = (error: unknown) => {
      if (settled) return
      settled = true
      stopTimers()
      reject(error)
    }

    const timeoutMessage = 'Timed out waiting for push subscription'

    const acceptIfUsable = (subscription: PushSubscription | null) => {
      if (subscription && canUsePushSubscription(subscription, applicationServerKey)) finish(subscription)
    }

    timeoutId = window.setTimeout(() => {
      pushManager
        .getSubscription()
        .then((subscription) => {
          acceptIfUsable(subscription)
          fail(new Error(timeoutMessage))
        })
        .catch(() => {
          fail(new Error(timeoutMessage))
        })
    }, PUSH_SUBSCRIBE_TIMEOUT_MS)

    pollDelayId = window.setTimeout(() => {
      pollId = window.setInterval(() => {
        if (settled) return

        pushManager
          .getSubscription()
          .then((subscription) => {
            // The subscription we already had is not proof that this subscribe() finished
            if (!subscription || subscription.endpoint === previousEndpoint) return
            acceptIfUsable(subscription)
          })
          .catch(() => {
            // getSubscription can fail while subscribe() is still in flight. Keep polling.
          })
      }, PUSH_SUBSCRIPTION_POLL_MS)
    }, PUSH_SUBSCRIPTION_POLL_DELAY_MS)

    pushManager
      .subscribe({
        userVisibleOnly: true,
        // WebKit rejects a Uint8Array view of a larger buffer; hand it a standalone ArrayBuffer
        applicationServerKey: standaloneApplicationServerKey(applicationServerKey),
      })
      .then((subscription) => {
        // Arriving after a timeout, it would be saved on the next page load even though the user saw a failure
        if (settled && !resolved) {
          reportLateSubscribeResult({
            elapsedMs: Date.now() - startedAt,
            outcome: 'resolved',
            endpointHost: new URL(subscription.endpoint).host,
          })
          subscription.unsubscribe().catch((error: unknown) => {
            console.error('Error unsubscribing late push subscription:', error)
          })
          return
        }
        finish(subscription)
      })
      .catch((error: unknown) => {
        if (settled && !resolved) {
          reportLateSubscribeResult({
            elapsedMs: Date.now() - startedAt,
            outcome: error instanceof Error ? `rejected ${error.name}: ${error.message}` : 'rejected',
          })
        }

        if (settled || (error instanceof DOMException && error.name === 'NotAllowedError')) {
          fail(error)
          return
        }

        // iPadOS can reject subscribe() after the subscription has already been stored
        pushManager
          .getSubscription()
          .then((subscription) => {
            if (subscription && subscription.endpoint !== previousEndpoint) {
              acceptIfUsable(subscription)
            }
            fail(error)
          })
          .catch(() => {
            fail(error)
          })
      })
  })
}

function standaloneApplicationServerKey(key: BufferSource): ArrayBuffer {
  const bytes = ArrayBuffer.isView(key)
    ? new Uint8Array(key.buffer, key.byteOffset, key.byteLength)
    : new Uint8Array(key)
  return bytes.slice().buffer
}

// Browsers that hide applicationServerKey still return a usable subscription for the key we just sent
function canUsePushSubscription(subscription: PushSubscription, applicationServerKey: BufferSource): boolean {
  if (!subscription.options?.applicationServerKey) return true
  return applicationServerKeysMatch(subscription, applicationServerKey)
}

function applicationServerKeysMatch(subscription: PushSubscription, expected: BufferSource): boolean {
  const current = subscription.options?.applicationServerKey
  if (!current) return false

  const currentBytes = new Uint8Array(current)
  const expectedBytes = ArrayBuffer.isView(expected)
    ? new Uint8Array(expected.buffer, expected.byteOffset, expected.byteLength)
    : new Uint8Array(expected)

  if (currentBytes.length !== expectedBytes.length) return false

  for (let index = 0; index < currentBytes.length; index += 1) {
    if (currentBytes[index] !== expectedBytes[index]) return false
  }

  return true
}

// Not every browser exposes the subscription's key; an unknown key must not cause an unsubscribe
function hasDifferentServerKey(subscription: PushSubscription, expected: BufferSource): boolean {
  return Boolean(subscription.options?.applicationServerKey) && !applicationServerKeysMatch(subscription, expected)
}

// The cached session can be empty because of a network error; only a successful empty response means signed out
async function isSessionEnded(): Promise<boolean> {
  const { data, error } = await authClient.getSession({ query: {} })
  return !(error || data)
}

// Helper function to convert base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String: string): BufferSource {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

export const usePushNotification = () => useContext(PushNotificationContext)
